import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Download, CheckCircle, Clock, Eye, FileDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const AllgemeinExport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exports, setExports] = useState([]);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Filter State
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [exportFormat, setExportFormat] = useState('summary');
  const [includeBarcode, setIncludeBarcode] = useState(true);
  
  // Complete Dialog
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completingExport, setCompletingExport] = useState(null);
  const [completeNote, setCompleteNote] = useState('');

  useEffect(() => {
    fetchData();
    
    // Set default dates (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  const fetchData = async () => {
    try {
      const [exportsRes, typesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/api/general-exports`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/general-writeoff-types`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/admin/locations`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setExports(exportsRes.data || []);
      setTypes(typesRes.data || []);
      setLocations(locationsRes.data || []);
      
      // Auto-select first location
      if (locationsRes.data?.length > 0) {
        setSelectedLocation(locationsRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!startDate || !endDate || !selectedLocation) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start-Datum muss vor End-Datum liegen');
      return;
    }
    
    setExporting(true);
    
    try {
      const response = await axios.post(
        `${API}/api/general-exports`,
        {
          customer_id: 'dummy',
          location_id: selectedLocation,
          start_date: startDate,
          end_date: endDate,
          export_type_ids: selectedTypes,
          export_format: exportFormat,
          include_barcode: includeBarcode,
          created_by: 'dummy'
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success(`Export erstellt! ${response.data.writeoff_count} Abschriften`);
      await fetchData();
      
    } catch (error) {
      console.error('Export failed:', error);
      const errorMsg = error.response?.data?.detail || 'Unbekannter Fehler';
      toast.error(`Fehler: ${errorMsg}`);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPDF = async (exportId) => {
    try {
      const response = await axios.get(`${API}/api/general-exports/${exportId}/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `allgemein_export_${exportId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF wird heruntergeladen');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Fehler beim Herunterladen');
    }
  };

  const handleCompleteExport = async () => {
    if (!completingExport) return;

    try {
      await axios.put(
        `${API}/api/general-exports/${completingExport.id}/complete`,
        null,
        {
          params: { note: completeNote },
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast.success('Export als abgeschlossen markiert');
      setShowCompleteDialog(false);
      setCompletingExport(null);
      setCompleteNote('');
      await fetchData();
    } catch (error) {
      console.error('Failed to complete export:', error);
      toast.error('Fehler beim Abschließen');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Lädt...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Allgemein Export
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">
            Export für allgemeine Abschriften erstellen
          </p>
        </div>

        {/* Export Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Neuen Export erstellen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Von *</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">Bis *</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Standort *</Label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger id="location">
                    <SelectValue placeholder="Standort auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Format & Barcode */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="format">Format</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger id="format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="summary">Zusammenfassung</SelectItem>
                      <SelectItem value="detailed">Detailliert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Barcode einschließen</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Switch
                      checked={includeBarcode}
                      onCheckedChange={setIncludeBarcode}
                    />
                    <span className="text-sm">{includeBarcode ? 'Ja' : 'Nein'}</span>
                  </div>
                </div>
              </div>

              {/* Export Button */}
              <Button 
                onClick={handleExport} 
                disabled={exporting || !selectedLocation}
                className="w-full sm:w-auto"
              >
                <FileDown className="h-4 w-4 mr-2" />
                {exporting ? 'Erstellt...' : 'Export erstellen'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export History */}
        <Card data-export-history>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Export-Historie</CardTitle>
          </CardHeader>
          <CardContent>
            {exports.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Noch keine Exporte erstellt</p>
              </div>
            ) : (
              <div className="space-y-3">
                {exports.map((exp) => (
                  <div
                    key={exp.id}
                    className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm sm:text-base">
                            {new Date(exp.start_date).toLocaleDateString('de-DE')} - {new Date(exp.end_date).toLocaleDateString('de-DE')}
                          </span>
                          <Badge variant={exp.status === 'completed' ? 'default' : 'secondary'}>
                            {exp.status === 'completed' ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Abgeschlossen
                              </>
                            ) : (
                              <>
                                <Clock className="h-3 w-3 mr-1" />
                                Ausstehend
                              </>
                            )}
                          </Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {exp.writeoff_count} Abschriften | {exp.export_format === 'summary' ? 'Zusammenfassung' : 'Detailliert'}
                        </p>
                        {exp.completion_note && (
                          <p className="text-xs text-blue-600 mt-1">
                            Notiz: {exp.completion_note}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/general-exports/${exp.id}`)}
                        >
                          <Eye className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">Ansehen</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPDF(exp.id)}
                        >
                          <Download className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">PDF</span>
                        </Button>
                        {exp.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setCompletingExport(exp);
                              setShowCompleteDialog(true);
                            }}
                          >
                            <CheckCircle className="h-4 w-4 sm:mr-1" />
                            <span className="hidden sm:inline">Abschließen</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Complete Dialog */}
      <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export abschließen</AlertDialogTitle>
            <AlertDialogDescription>
              Markieren Sie diesen Export als abgeschlossen, wenn die Daten in Ihr WWS übertragen wurden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="note" className="text-sm">Notiz (optional)</Label>
            <Input
              id="note"
              placeholder="z.B. In WWS übertragen am..."
              value={completeNote}
              onChange={(e) => setCompleteNote(e.target.value)}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setCompletingExport(null);
              setCompleteNote('');
            }}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleCompleteExport}>
              Als abgeschlossen markieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};
